// ============================================================================
// Embed Config — types and resolution
// ============================================================================

import type { ChatTheme, DocumentUploadConfig } from "../@types";
import type { Locale, LocaleMessageOverrides, MessageOverrides } from "../i18n";
import type { VisitorIdInput } from "../lib/visitor-context";
import type { VisibilityRules } from "./visibility";
import type { WidgetEvent } from "./widget-events";

/**
 * Built-in theme presets. `auto` follows the host's `prefers-color-scheme`
 * and switches at runtime without re-rendering.
 */
export type ThemePreset = "light" | "dark" | "auto";

/**
 * Appearance config for the chat widget. Pick a preset and (optionally) layer
 * per-property `variables` on top.
 *
 * ```ts
 * appearance: { theme: "dark", variables: { primaryColor: "#ff6b6b" } }
 * ```
 *
 * The same shape is accepted by the `embed.js` script (`init({ appearance })`),
 * `<WaniwaniChat overrides={{ appearance }} />`, and `<ChatEmbed appearance />`.
 */
export interface ChatAppearance {
	/** Base theme preset. Defaults to `"light"`. */
	theme?: ThemePreset;
	/** Per-property overrides applied on top of the preset. */
	variables?: ChatTheme;
	/**
	 * Render assistant messages inside a filled bubble (background + padding +
	 * radius), styled by `variables.assistantBubbleColor` /
	 * `assistantBubbleTextColor`. Defaults to `false` — assistant messages
	 * render as plain text. Opt in for a "both sides bubbled" chat style.
	 */
	assistantBubble?: boolean;
}

/**
 * Visual treatment of the in-flow composer (`mode: "composer"`). The surface
 * is page content, so it has to sit inside designs the SDK never sees: a hero
 * band, a card in a help center, a row of filters.
 *
 * - `"glow"` (default) — filled box on a card shadow, with the one-off border
 *   glow sweep once the config resolves. The attention-grabbing option.
 * - `"outline"` — the same box without the sweep. Sits quietly inside a card
 *   or a form that already carries its own chrome.
 *
 * Two, because a variant is a starting point rather than the ceiling. Radius,
 * fill, border, shadow and the send button read from `--ww-composer-*` CSS
 * custom properties, so a host restyles the box from its own stylesheet
 * instead of waiting for a variant here (a fully rounded search bar is
 * `--ww-composer-radius: 9999px`). See the chat embed docs for the list.
 */
export type ComposerVariant = "glow" | "outline";

/** Composer scale. Padding, text size and send-button size move together. */
export type ComposerSize = "sm" | "md" | "lg";

/**
 * Presentation options for the `mode: "composer"` surface. Every field is
 * cosmetic — behavior (first message opens the panel) is the same throughout.
 */
export interface ComposerAppearance {
	/** Visual treatment. Defaults to `"glow"`. */
	variant?: ComposerVariant;
	/** Scale. Defaults to `"md"`. */
	size?: ComposerSize;
	/**
	 * Render the channel's suggestion pills under the box. Defaults to `true`;
	 * set `false` for a bare input in a tight layout.
	 */
	showSuggestions?: boolean;
	/**
	 * Horizontal alignment of the suggestion pills, and of the label when the
	 * composer renders as a trigger. Defaults to `"start"`.
	 */
	align?: "start" | "center";
	/**
	 * Render the composer as a tap target instead of a live input: nothing is
	 * typed in the page, and a click opens the panel where the caret lands.
	 * Defaults to `false`.
	 *
	 * For hosts that want one tap into the full chat and no in-page composing.
	 * The box keeps its variant's chrome and whatever CSS is set on it — only
	 * the textarea is swapped for the trigger. `mobileTrigger` applies the same
	 * swap on phones only; this one applies at every width.
	 */
	trigger?: boolean;
	/**
	 * On phone-width viewports, render the composer as a tap target instead of
	 * a live input: one tap opens the full-screen panel and types there.
	 * Defaults to `true`.
	 *
	 * Typing into an in-page box on a phone is the worse half of the handoff —
	 * the soft keyboard covers the composer, the first message opens a
	 * full-screen panel over it, and focus has to move mid-sentence. Going
	 * straight to the panel skips all of it. The box keeps its variant's
	 * chrome and whatever CSS is set on it; only the input is swapped for the
	 * trigger. Set `false` to keep a real input at every width.
	 */
	mobileTrigger?: boolean;
}

/**
 * Tool-call activity rendering mode. Steps are grouped into one collapsible
 * "chain of thought".
 *
 * - `true` (default) — each step expands to its request/response JSON.
 * - `"titles-only"` — step labels only, no JSON.
 * - `false` — the chain (and the reasoning trace) are hidden entirely; only
 *   a generic working indicator shows while the agent works.
 */
export type ShowToolCalls = boolean | "titles-only";

export const AVATAR_SLOTS = [
	"welcome",
	"header",
	"message",
	"composer",
	"dock",
] as const;

/** Where the avatar image may appear. */
export type AvatarSlot = (typeof AVATAR_SLOTS)[number];

function isAvatarSlot(value: string): value is AvatarSlot {
	return (AVATAR_SLOTS as readonly string[]).includes(value);
}

export const DOCK_CHIPS_TRIGGERS = ["hover", "scroll", "always"] as const;

export type DockChipsTrigger = (typeof DOCK_CHIPS_TRIGGERS)[number];

export type DockClick = "expand" | "open";

export const MENU_ACTIONS = [
	"new-chat",
	"restart",
	"copy-conversation",
	"history",
	"delete",
] as const;

/** An entry of the header menu, in the order given. */
export type MenuAction = (typeof MENU_ACTIONS)[number];

function isMenuAction(value: string): value is MenuAction {
	return (MENU_ACTIONS as readonly string[]).includes(value);
}

/** `"a|b|"` → `["a", "b"]`: trimmed, empty entries dropped. */
export function splitPipeList(raw: string): string[] {
	return raw
		.split("|")
		.map((part) => part.trim())
		.filter(Boolean);
}

/** Intent tier of a page prompt (discover/compare/act); one shown per tier. */
export type PagePromptTier = "low" | "medium" | "high";

/**
 * One authored page prompt from `/config`. `id` is the entry's stable
 * identity; untagged (`tier` absent) prompts fill any tier slot.
 */
export interface PagePrompt {
	id: string | null;
	text: string;
	tier?: PagePromptTier;
}

/** Starter prompt set for one page, keyed by normalized pathname. */
export interface PageSuggestionsEntry {
	url: string;
	prompts: PagePrompt[];
}

/**
 * Configuration for the embeddable chat widget.
 *
 * Resolution priority (later wins):
 *   defaults < remote config (from server) < `data-*` attrs < programmatic.
 *
 * Remote config lives on `GET {api}/config` and is authenticated with the
 * public token. It only carries display-facing fields (title, welcome message,
 * placeholder, suggestions) — system prompt and step budget stay server-side.
 */
/**
 * Where the page finds the MCP server whose tools it publishes to a browsing
 * agent, and whether to publish them at all.
 */
export interface WebMcpEmbedConfig {
	/**
	 * On unless explicitly switched off.
	 *
	 * The page's own switch, for markup that wants the chat but not the tools on
	 * this particular page.
	 */
	enabled?: boolean;
}

export interface EmbedConfig {
	/** Waniwani chat API URL. Defaults to `https://app.waniwani.ai/api/mcp/chat`. */
	api?: string;
	/** Public token (wwp_...) for authentication (required). */
	token: string;
	/**
	 * Override the anonymous visitor id the widget generates with one you
	 * already track — a PostHog / Amplitude / Segment distinct id, your own
	 * first-party cookie, etc. The chat sends it on every request, so Waniwani
	 * sessions and events correlate to the same visitor the host site tracks,
	 * and server-side MCP tools and flows read it back as
	 * `context.waniwani.visitorId`. Surfaced as `data-visitor-id` on the embed
	 * script tag (string only there).
	 *
	 * Passed to `init()` it may also be a resolver returning the id — sync
	 * (`() => posthog.get_distinct_id()`) or async — for analytics SDKs whose id
	 * is only ready after they bootstrap. A blank / failed result is ignored. For
	 * an id that resolves later still, call `WaniWani.chat.setVisitorId(id)`.
	 */
	visitorId?: VisitorIdInput;
	/** Override MCP server URL (optional — resolved from environment by default). */
	mcpServerUrl?: string;
	/**
	 * WebMCP: publish this site's MCP tools to a browsing agent standing on the
	 * page, via `document.modelContext`.
	 *
	 * A third surface for the same flows, alongside the connector and this chat.
	 * On by default, and needs no configuration: the token identifies the
	 * environment and the hosted API resolves its MCP server per request, the
	 * same way `/config` and `/resource` already do.
	 *
	 * Surfaced as `data-webmcp` on the script tag.
	 */
	webmcp?: WebMcpEmbedConfig;
	/**
	 * Report a page view that shows signs of browser automation (a WebDriver
	 * flag, a headless browser, automation globals, input no person makes) to
	 * the site's agent traffic. It sends the names of the signals seen, the page
	 * URL without its query and the referrer's origin and path, and only when a
	 * signal fires. No cookie, no stored id.
	 *
	 * Once a signal fires, it also adds one optional field at the start of
	 * every form: "In one short sentence, what is the intent of this visit?".
	 * An agent's answer is reported with the signals when the form is
	 * submitted. A page with no signal is never changed. The field never blocks
	 * a submit, has no name and never reaches the form's own data. A form with
	 * `data-waniwani-no-intent` gets no field, and neither does a page whose
	 * user agent names a crawler, an indexer or a link preview.
	 *
	 * Off by default. Surfaced as `data-agent-beacon` on the script tag
	 * (`"on"`, `"true"`, `"1"` or the bare attribute turns it on).
	 */
	agentBeacon?: boolean;
	/**
	 * Agent channel ID. Sent to the chat API so the Waniwani app routes the
	 * conversation to the right agent. Surfaced as `data-channel-id` on the
	 * embed script tag.
	 */
	channelId?: string;
	/**
	 * Channel-specific event source. Comes from the remote `/config` response
	 * (not author-set) and is stamped onto widget-originated events such as
	 * `page.viewed` so they attribute to this channel's source rather than a
	 * generic `"widget"` literal.
	 */
	source?: string;
	/**
	 * How tool-call activity renders, grouped into one collapsible chain:
	 * `true` (default) makes each step expandable to its request/response
	 * JSON, `"titles-only"` shows step labels only, `false` hides the chain
	 * and the reasoning trace entirely.
	 *
	 * Surfaced as `data-show-tool-calls` on the embed script tag.
	 */
	showToolCalls?: ShowToolCalls;
	/** Title shown in the chat header. Defaults to `"Assistant"`. */
	title?: string;
	/**
	 * Force-hide the sticky header even when `title` or `enableThreadHistory`
	 * would otherwise show it. Useful when the host page already provides its
	 * own chrome. Surfaced as `data-hide-header` on the embed script tag.
	 */
	hideHeader?: boolean;
	/** Welcome message shown before the first user message. */
	welcomeMessage?: string;
	/**
	 * URL of the assistant's avatar image. Shown in `avatarSlots`. Surfaced as
	 * `data-avatar`.
	 */
	avatar?: string;
	/**
	 * Where the avatar appears. Defaults to `["welcome", "header"]` when
	 * `avatar` is set. Surfaced as `data-avatar-slots` (comma-separated).
	 */
	avatarSlots?: AvatarSlot[];
	/**
	 * URL of an animated image (GIF, animated WebP, APNG) played once per slot
	 * before `avatar` takes over. Surfaced as `data-avatar-intro`.
	 */
	avatarIntro?: string;
	/**
	 * How long `avatarIntro` plays, in ms, counted from the moment it loads.
	 * Defaults to 7000. Surfaced as `data-avatar-intro-duration`.
	 */
	avatarIntroDuration?: number;
	/**
	 * Title of the structured welcome shown while the conversation is empty,
	 * in place of the `welcomeMessage` bubble. A `|` starts a new line. When
	 * unset, `messages.welcome.title` fills it. Surfaced as
	 * `data-welcome-title`.
	 */
	welcomeTitle?: string;
	/**
	 * Subtitles under the welcome title. More than one rotates every
	 * `welcomeSubtitleRotate` ms. Surfaced as `data-welcome-subtitle`
	 * (`|`-separated).
	 */
	welcomeSubtitle?: string[];
	/**
	 * Milliseconds between welcome subtitle swaps. Unset or `0` keeps the
	 * first one. Surfaced as `data-welcome-subtitle-rotate`.
	 */
	welcomeSubtitleRotate?: number;
	/** Placeholder text for the input field. */
	placeholder?: string;
	/** Initial suggestion chips displayed before the first message. */
	suggestions?: string[];
	/**
	 * Per-page starter prompt sets from `/config`; the widget picks from the
	 * entry matching its pathname, falling back to `suggestions`. No `data-*`
	 * attribute maps to it.
	 */
	pageSuggestions?: PageSuggestionsEntry[];
	/**
	 * AI transparency notice rendered under the input (EU AI Act compliance).
	 * String overrides the default wording; `false` hides it. Surfaced as
	 * `data-disclaimer` on the embed script tag (use `data-disclaimer="false"`
	 * to hide).
	 */
	disclaimer?: string | false;
	/** Surfaced as `data-suggestions-layout`: `wrap` (default) or `single-row`. */
	suggestionsLayout?: "wrap" | "single-row";
	/**
	 * Surfaced as `data-suggestions-position`: `above-input` (default) or
	 * `after-message`. The pre-chat row stays above the input either way.
	 */
	suggestionsPosition?: "above-input" | "after-message";
	/** Surfaced as `data-send-icon`: `up` (default) or `right`. */
	sendIcon?: "up" | "right";
	/**
	 * Header menu entries, in order. When set, one menu replaces the New chat
	 * and thread history buttons. Surfaced as `data-menu` (comma-separated:
	 * `new-chat`, `restart`, `copy-conversation`, `history`, `delete`).
	 */
	menu?: MenuAction[];
	/**
	 * Ask before the menu's `restart` clears the conversation. Surfaced as
	 * `data-restart-confirm`.
	 */
	restartConfirm?: boolean;
	/**
	 * When the floating bar's suggestion chips show, as a list whose triggers
	 * combine (the first to fire wins). `hover` (default) expands them a beat
	 * after the bar appears and on focus; `scroll` adds a pause after
	 * scrolling down into a section, once per section; `always` keeps them
	 * expanded from the moment the bar appears. A single string is accepted
	 * and treated as a one-element list. Surfaced as `data-dock-chips-trigger`,
	 * a comma-separated list such as `hover,scroll`.
	 */
	dockChipsTrigger?: DockChipsTrigger[];
	/**
	 * Write `--ww-pointer-x` and `--ww-pointer-y` (px, relative to the bar) on
	 * `ww-dock` while the pointer moves over it. Off by default. Surfaced as
	 * `data-dock-pointer-vars`.
	 */
	dockPointerVars?: boolean;
	/**
	 * `false` renders the floating bar's input without the animated border
	 * glow. On by default. Surfaced as `data-dock-glow`.
	 */
	dockGlow?: boolean;
	/**
	 * Make the floating bar's input read-only on narrow viewports, so a tap
	 * opens the panel without raising the keyboard. Off by default. Surfaced
	 * as `data-dock-readonly-mobile`.
	 */
	dockReadonlyMobile?: boolean;
	/**
	 * On narrow viewports, hold one browser history entry while the panel is
	 * open so the back gesture closes it instead of leaving the page. Off by
	 * default. Surfaced as `data-panel-history`.
	 */
	panelHistory?: boolean;
	/**
	 * What a click or focus on the floating bar's input does before any
	 * conversation exists. `expand` (default) widens the bar and shows the
	 * chips; `open` opens the panel, as a tap does on phones. Surfaced as
	 * `data-dock-click`.
	 */
	dockClick?: DockClick;
	/**
	 * `false` hides the copy and download toolbar above tables in answers.
	 * Shown by default. Surfaced as `data-show-table-toolbar`.
	 */
	showTableToolbar?: boolean;
	/** @internal Enterprise only: hides the "powered by Waniwani" logo. */
	hidePoweredBy?: boolean;
	/** URL to a custom stylesheet injected into the shadow root. */
	css?: string;
	/**
	 * Persist conversations across page reloads using IndexedDB so users can
	 * resume previous threads. Defaults to `false` — opt in explicitly.
	 */
	enableThreadHistory?: boolean;
	/**
	 * Documents module state, served by the platform. The composer offers the
	 * paperclip, the drop target and paste only while `enabled` is true.
	 */
	documentUpload?: DocumentUploadConfig;
	/** Where a chat opens a session with the agent runtime directly. Set by the platform's remote config only. */
	sessionApi?: string;
	/**
	 * The channel's custom metadata, as `/config` serves it. Timing beacons read
	 * it from the remote config only; setting it here sends nothing.
	 * @internal
	 */
	metadata?: Record<string, string>;
	/**
	 * Theme preset + per-property overrides. The script tag exposes the
	 * preset via `data-theme="light|dark|auto"`; programmatic callers can
	 * additionally pass `variables` to tweak individual colours.
	 */
	appearance?: ChatAppearance;
	/**
	 * UI language for built-in labels (placeholders, status text, buttons).
	 * One of `"en"`, `"fr"`, `"es"`. When omitted, the widget detects the
	 * locale from `<html lang>` / `navigator.language` and falls back to
	 * English. Surfaced as `data-locale` on the embed script tag.
	 */
	locale?: Locale;
	/**
	 * Overrides for any built-in string, flat (applies to every locale) or
	 * keyed by locale (`{ "fr": { "menu": { "restart": "…" } } }`). Layered
	 * over the map fetched from `messagesUrl`; these win per key. Surfaced as
	 * `data-messages` (JSON) on the embed script tag.
	 */
	messages?: MessageOverrides | LocaleMessageOverrides;
	/**
	 * URL of a JSON file with the same shape as `messages`, fetched on mount.
	 * The chat waits for it (up to a short timeout) before it paints, so the
	 * built-in wording never flashes. Surfaced as `data-messages-url`.
	 */
	messagesUrl?: string;
	/**
	 * How the widget renders.
	 *
	 * - `"inline"` (default) mounts the chat into a `[data-waniwani-embed]`
	 *   element on the page. If none exists, one is created and inserted in
	 *   front of the embed `<script>` tag, so a bare snippet works with no
	 *   markup changes.
	 * - `"floating"` renders a launcher pill anchored to a corner that opens
	 *   the chat in a panel — a fixed-size panel on desktop, full-screen on
	 *   mobile. No `[data-waniwani-embed]` element is needed.
	 * - `"composer"` mounts just the input (plus the channel's suggestion
	 *   pills) into `[data-waniwani-embed]`, sized by your own CSS — a hero
	 *   search box, a card in a help page. Sending the first message opens the
	 *   same panel `"floating"` uses, over the page.
	 * - `"off"` mounts no chat surface at all: no dock, no inline box, no
	 *   panel. The script still publishes the site's tools over WebMCP and
	 *   still exposes `WaniWani.chat.track` / `.identify` on the host page, so
	 *   a site can carry the tag (and be reachable by a browsing agent)
	 *   without showing a widget to its visitors. The chat methods
	 *   (`open()`, `sendMessage()`, …) are no-ops, and no `page.viewed` event
	 *   fires because no chat loads its channel config.
	 *
	 * Surfaced as `data-mode` on the embed script tag.
	 */
	mode?: "inline" | "floating" | "composer" | "off";
	/**
	 * Presentation of the in-flow composer. Only applies when `mode` is
	 * `"composer"`. Surfaced as `data-composer-variant`, `data-composer-size`,
	 * `data-composer-suggestions`, `data-composer-align`,
	 * `data-composer-trigger` and `data-composer-mobile-trigger` on the embed
	 * script tag.
	 */
	composer?: ComposerAppearance;
	/**
	 * Default height for the inline embed container. Any CSS length
	 * (`"500px"`, `"80vh"`, …) or a bare number (treated as `px`). Applied to
	 * the `[data-waniwani-embed]` container unless you size it yourself in
	 * CSS. Defaults to `500px`. Ignored in `floating` and `composer` mode,
	 * where the surface is sized by its content. Surfaced as `data-height`.
	 */
	height?: string;
	/**
	 * Text shown on the chat's entry input (e.g. `"Ask anything…"`) — the
	 * floating dock's bar, or the in-flow box in `composer` mode. Overrides
	 * `placeholder` there, leaving the in-panel input's own placeholder alone.
	 * Only applies when `mode` is `"floating"` or `"composer"`. Defaults to a
	 * localized prompt. Surfaced as `data-launcher-text`.
	 */
	launcherText?: string;
	/**
	 * Delay in milliseconds before the floating dock animates into view after
	 * the page renders. Letting the page settle first keeps the bar from
	 * competing with the host's initial content. Defaults to `2000`. `0` shows
	 * it immediately. Only applies when `mode` is `"floating"` — a `composer`
	 * surface is page content and renders with the page. Surfaced as
	 * `data-appear-delay`.
	 */
	appearDelay?: number;
	/**
	 * Whether the floating dock slides away at the bottom of the page so it
	 * doesn't cover the host's footer. On by default: the dock hides once the
	 * page's `<footer>` (or `[role="contentinfo"]`) scrolls into view, or
	 * within 120px of the end of the page when there's no footer, and comes
	 * back as soon as the visitor scrolls up. `false` keeps the dock pinned
	 * the whole way down. A number hides it within that many px of the end of
	 * the page instead of tracking the footer. Never hides the open chat
	 * panel, or the dock while the visitor has text typed in it. Only applies
	 * when `mode` is `"floating"`. Surfaced as `data-hide-at-bottom`
	 * (`"false"` or a pixel distance).
	 */
	hideAtBottom?: boolean | number;
	/**
	 * Opt out of the top-of-funnel `page.viewed` event the widget fires once
	 * on mount. Leave unset (or `false`) to keep the default landing-funnel
	 * tracking; set `true` on surfaces where a page view is meaningless and
	 * would pollute the customer's funnel (internal tools, previews, an
	 * already-authenticated app shell, etc.). Surfaced as
	 * `data-disable-page-view` on the embed script tag.
	 */
	disablePageView?: boolean;
	/**
	 * Per-URL show/hide rules for the widget. Comes from the remote `/config`
	 * response (not author-set). When set, the surface only renders on paths
	 * the rules resolve to `"show"`; in `inline` and `composer` mode the
	 * `[data-waniwani-embed]` container collapses too, so a gated page shows no
	 * empty box. Unset/`null` means show on every page (default behavior).
	 */
	visibility?: VisibilityRules;
	/**
	 * Host-page callback fired on chat lifecycle events (`chat.ready`,
	 * `chat.opened`/`chat.closed` in floating mode, `message.sent`,
	 * `message.received`, `session.started`, `thread.changed`, `chat.error`,
	 * `suggestion.clicked`, `suggestions.shown`, `link.clicked`,
	 * `conversation.copied`, `conversation.restarted`). Message events never include the
	 * message text. The embed runs in the host page's DOM, so the callback can
	 * forward events directly into the page's analytics.
	 *
	 * Programmatic-only: a function cannot be expressed as a `data-*`
	 * attribute, so pass it to `WaniWani.chat.init()`. Exceptions thrown by
	 * the callback are swallowed and never break the widget.
	 */
	onEvent?: (event: WidgetEvent) => void;
}

/** Fallback height applied to an inline embed container with no author sizing. */
export const DEFAULT_EMBED_HEIGHT = "500px";

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_API_URL = "https://app.waniwani.ai/api/mcp/chat";

const DEFAULTS = {
	api: DEFAULT_API_URL,
};

// ---------------------------------------------------------------------------
// Script tag detection
// ---------------------------------------------------------------------------

export function findScriptTag(): HTMLScriptElement | null {
	if (typeof document === "undefined") {
		return null;
	}

	// Preferred: works in synchronous execution during script load
	if (document.currentScript instanceof HTMLScriptElement) {
		return document.currentScript;
	}

	// Fallback for async / late-injected execution, where `currentScript` is
	// null. Prefer a script that actually carries config (`data-token`): when a
	// loader (app.waniwani.ai/embed.js) injects this bundle, both scripts match
	// `/embed/`, but the loader strips its own `data-token`, so this pins the
	// real config carrier regardless of DOM order.
	const withToken = document.querySelector("script[src][data-token]");
	if (withToken instanceof HTMLScriptElement) {
		return withToken;
	}

	// Last resort: first script whose src looks like the embed bundle.
	const scripts = document.querySelectorAll("script[src]");
	for (const script of scripts) {
		if (script instanceof HTMLScriptElement && /embed/i.test(script.src)) {
			return script;
		}
	}

	return null;
}

// ---------------------------------------------------------------------------
// Parse data-* attributes from the script element
// ---------------------------------------------------------------------------

export function parseConfigFromScript(): Partial<EmbedConfig> {
	const el = findScriptTag();
	if (!el) {
		return {};
	}

	const str = (attr: string): string | undefined =>
		el.getAttribute(attr) ?? undefined;

	const bool = (attr: string): boolean | undefined => {
		const raw = el.getAttribute(attr);
		if (raw == null) {
			return undefined;
		}
		const lowered = raw.trim().toLowerCase();
		if (lowered === "true" || lowered === "1" || lowered === "") {
			return true;
		}
		if (lowered === "false" || lowered === "0") {
			return false;
		}
		return undefined;
	};

	const config: Partial<EmbedConfig> = {};

	const api = str("data-api");
	if (api) {
		config.api = api;
	}

	const token = str("data-token");
	if (token) {
		config.token = token;
	}

	const visitorId = str("data-visitor-id");
	if (visitorId) {
		config.visitorId = visitorId;
	}

	const title = str("data-title");
	if (title) {
		config.title = title;
	}

	const welcomeMessage = str("data-welcome-message");
	if (welcomeMessage) {
		config.welcomeMessage = welcomeMessage;
	}

	const avatar = str("data-avatar")?.trim();
	if (avatar) {
		config.avatar = avatar;
	}

	const avatarSlots = str("data-avatar-slots")
		?.split(",")
		.map((slot) => slot.trim().toLowerCase())
		.filter(isAvatarSlot);
	if (avatarSlots?.length) {
		config.avatarSlots = avatarSlots;
	}

	const avatarIntro = str("data-avatar-intro")?.trim();
	if (avatarIntro) {
		config.avatarIntro = avatarIntro;
	}

	const avatarIntroDuration = Number.parseInt(
		str("data-avatar-intro-duration")?.trim() ?? "",
		10,
	);
	if (Number.isFinite(avatarIntroDuration) && avatarIntroDuration > 0) {
		config.avatarIntroDuration = avatarIntroDuration;
	}

	const welcomeTitle = str("data-welcome-title")?.trim();
	if (welcomeTitle) {
		config.welcomeTitle = welcomeTitle;
	}

	const welcomeSubtitle = splitPipeList(str("data-welcome-subtitle") ?? "");
	if (welcomeSubtitle.length > 0) {
		config.welcomeSubtitle = welcomeSubtitle;
	}

	const welcomeSubtitleRotate = Number.parseInt(
		str("data-welcome-subtitle-rotate")?.trim() ?? "",
		10,
	);
	if (Number.isFinite(welcomeSubtitleRotate) && welcomeSubtitleRotate >= 0) {
		config.welcomeSubtitleRotate = welcomeSubtitleRotate;
	}

	const placeholder = str("data-placeholder");
	if (placeholder) {
		config.placeholder = placeholder;
	}

	const mcpServerUrl = str("data-mcp-server-url");
	if (mcpServerUrl) {
		config.mcpServerUrl = mcpServerUrl;
	}

	// Declines the surface on a page that should stay quiet, without touching
	// the channel every other page shares.
	const webmcpFlag = str("data-webmcp");
	if (webmcpFlag) {
		config.webmcp = {
			enabled: webmcpFlag !== "false" && webmcpFlag !== "off",
		};
	}

	const agentBeaconFlag = str("data-agent-beacon")?.trim().toLowerCase();
	if (agentBeaconFlag !== undefined) {
		config.agentBeacon = ["on", "true", "1", ""].includes(agentBeaconFlag);
	}

	const channelId = str("data-channel-id");
	if (channelId) {
		config.channelId = channelId;
	}

	const showToolCallsRaw = str("data-show-tool-calls")?.trim().toLowerCase();
	if (showToolCallsRaw === "titles-only") {
		config.showToolCalls = "titles-only";
	} else {
		const showToolCalls = bool("data-show-tool-calls");
		if (showToolCalls !== undefined) {
			config.showToolCalls = showToolCalls;
		}
	}

	const css = str("data-css");
	if (css) {
		config.css = css;
	}

	const suggestionsRaw = str("data-suggestions");
	if (suggestionsRaw) {
		config.suggestions = suggestionsRaw
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
	}

	const enableThreadHistory = bool("data-enable-thread-history");
	if (enableThreadHistory !== undefined) {
		config.enableThreadHistory = enableThreadHistory;
	}

	const disablePageView = bool("data-disable-page-view");
	if (disablePageView !== undefined) {
		config.disablePageView = disablePageView;
	}

	const hideHeader = bool("data-hide-header");
	if (hideHeader !== undefined) {
		config.hideHeader = hideHeader;
	}

	const hidePoweredBy = bool("data-hide-powered-by");
	if (hidePoweredBy !== undefined) {
		config.hidePoweredBy = hidePoweredBy;
	}

	const themeRaw = str("data-theme");
	if (themeRaw === "light" || themeRaw === "dark" || themeRaw === "auto") {
		config.appearance = { ...config.appearance, theme: themeRaw };
	}

	const assistantBubble = bool("data-assistant-bubble");
	if (assistantBubble !== undefined) {
		config.appearance = { ...config.appearance, assistantBubble };
	}

	const localeRaw = str("data-locale");
	if (localeRaw === "en" || localeRaw === "fr" || localeRaw === "es") {
		config.locale = localeRaw;
	}

	const messages = parseMessagesAttr(str("data-messages"));
	if (messages) {
		config.messages = messages;
	}

	const messagesUrl = str("data-messages-url")?.trim();
	if (messagesUrl) {
		config.messagesUrl = messagesUrl;
	}

	const modeRaw = str("data-mode");
	if (
		modeRaw === "inline" ||
		modeRaw === "floating" ||
		modeRaw === "composer" ||
		modeRaw === "off"
	) {
		config.mode = modeRaw;
	}

	const composer: ComposerAppearance = {};
	const composerVariant = str("data-composer-variant")?.trim().toLowerCase();
	if (composerVariant === "glow" || composerVariant === "outline") {
		composer.variant = composerVariant;
	}
	const composerSize = str("data-composer-size")?.trim().toLowerCase();
	if (composerSize === "sm" || composerSize === "md" || composerSize === "lg") {
		composer.size = composerSize;
	}
	const composerSuggestions = bool("data-composer-suggestions");
	if (composerSuggestions !== undefined) {
		composer.showSuggestions = composerSuggestions;
	}
	const composerAlign = str("data-composer-align")?.trim().toLowerCase();
	if (composerAlign === "start" || composerAlign === "center") {
		composer.align = composerAlign;
	}
	const composerTrigger = bool("data-composer-trigger");
	if (composerTrigger !== undefined) {
		composer.trigger = composerTrigger;
	}
	const composerMobileTrigger = bool("data-composer-mobile-trigger");
	if (composerMobileTrigger !== undefined) {
		composer.mobileTrigger = composerMobileTrigger;
	}
	if (Object.keys(composer).length > 0) {
		config.composer = composer;
	}

	const height = str("data-height");
	if (height) {
		config.height = height;
	}

	const launcherText = str("data-launcher-text");
	if (launcherText) {
		config.launcherText = launcherText;
	}

	const appearDelayRaw = str("data-appear-delay");
	if (appearDelayRaw !== undefined) {
		const parsed = Number.parseInt(appearDelayRaw.trim(), 10);
		if (Number.isFinite(parsed) && parsed >= 0) {
			config.appearDelay = parsed;
		}
	}

	const hideAtBottom = parseHideAtBottom(str("data-hide-at-bottom"));
	if (hideAtBottom !== undefined) {
		config.hideAtBottom = hideAtBottom;
	}

	const disclaimerRaw = str("data-disclaimer");
	if (disclaimerRaw !== undefined) {
		const trimmed = disclaimerRaw.trim();
		const lowered = trimmed.toLowerCase();
		if (lowered === "false" || lowered === "0") {
			config.disclaimer = false;
		} else if (trimmed) {
			config.disclaimer = trimmed;
		}
	}

	const suggestionsLayout = str("data-suggestions-layout")?.trim();
	if (suggestionsLayout === "wrap" || suggestionsLayout === "single-row") {
		config.suggestionsLayout = suggestionsLayout;
	}

	const suggestionsPosition = str("data-suggestions-position")?.trim();
	if (
		suggestionsPosition === "above-input" ||
		suggestionsPosition === "after-message"
	) {
		config.suggestionsPosition = suggestionsPosition;
	}

	const sendIcon = str("data-send-icon")?.trim();
	if (sendIcon === "up" || sendIcon === "right") {
		config.sendIcon = sendIcon;
	}

	const menu = str("data-menu")
		?.split(",")
		.map((action) => action.trim().toLowerCase())
		.filter(isMenuAction);
	if (menu?.length) {
		config.menu = [...new Set(menu)];
	}

	const restartConfirm = bool("data-restart-confirm");
	if (restartConfirm !== undefined) {
		config.restartConfirm = restartConfirm;
	}

	const dockChipsTriggers = [
		...new Set(
			(str("data-dock-chips-trigger") ?? "")
				.split(",")
				.map((value) => value.trim().toLowerCase())
				.filter((value): value is DockChipsTrigger =>
					(DOCK_CHIPS_TRIGGERS as readonly string[]).includes(value),
				),
		),
	];
	if (dockChipsTriggers.length > 0) {
		config.dockChipsTrigger = dockChipsTriggers;
	}

	const dockPointerVars = bool("data-dock-pointer-vars");
	if (dockPointerVars !== undefined) {
		config.dockPointerVars = dockPointerVars;
	}

	const dockGlow = bool("data-dock-glow");
	if (dockGlow !== undefined) {
		config.dockGlow = dockGlow;
	}

	const dockReadonlyMobile = bool("data-dock-readonly-mobile");
	if (dockReadonlyMobile !== undefined) {
		config.dockReadonlyMobile = dockReadonlyMobile;
	}

	const panelHistory = bool("data-panel-history");
	if (panelHistory !== undefined) {
		config.panelHistory = panelHistory;
	}

	const dockClick = str("data-dock-click")?.trim().toLowerCase();
	if (dockClick === "expand" || dockClick === "open") {
		config.dockClick = dockClick;
	}

	const showTableToolbar = bool("data-show-table-toolbar");
	if (showTableToolbar !== undefined) {
		config.showTableToolbar = showTableToolbar;
	}

	return config;
}

function parseMessagesAttr(
	raw: string | undefined,
): MessageOverrides | LocaleMessageOverrides | undefined {
	if (raw === undefined) {
		return undefined;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (
			typeof parsed === "object" &&
			parsed !== null &&
			!Array.isArray(parsed)
		) {
			return parsed as MessageOverrides | LocaleMessageOverrides;
		}
	} catch {}
	console.warn("[Waniwani] Invalid data-messages JSON");
	return undefined;
}

/**
 * `data-hide-at-bottom`: `"true"` / `"false"`, or a non-negative pixel
 * distance (`"200"`). Anything else is ignored.
 */
function parseHideAtBottom(
	raw: string | undefined,
): boolean | number | undefined {
	if (raw === undefined) {
		return undefined;
	}
	const value = raw.trim().toLowerCase();
	if (value === "" || value === "true") {
		return true;
	}
	if (value === "false") {
		return false;
	}
	const px = Number(value);
	return Number.isFinite(px) && px >= 0 ? px : undefined;
}

// ---------------------------------------------------------------------------
// Merge: defaults < remote < script attrs < programmatic
// ---------------------------------------------------------------------------

/**
 * Drop keys whose value is `undefined` so a spread doesn't overwrite an
 * already-resolved value with nothing. Callers (notably `WaniwaniChat`)
 * build `programmatic` by reading `overrides?.welcomeMessage` etc. — every
 * unset override comes out as `{welcomeMessage: undefined, …}` and the
 * trailing `...programmatic` would otherwise blow away values from earlier
 * layers (defaults / remote / script).
 */
function compact<T extends object>(obj: T | undefined): Partial<T> {
	if (!obj) {
		return {};
	}
	const out: Partial<T> = {};
	for (const key in obj) {
		if (obj[key] !== undefined) {
			out[key] = obj[key];
		}
	}
	return out;
}

export function resolveConfig(
	programmatic?: Partial<EmbedConfig>,
	remote?: Partial<EmbedConfig>,
	scriptConfig?: Partial<EmbedConfig>,
): EmbedConfig {
	// Caller may pass a pre-parsed script config so async re-resolution
	// (post-fetch) doesn't re-invoke `parseConfigFromScript` — by the time a
	// promise settles, `document.currentScript` is null and the fallback
	// heuristic can miss renamed/CDN-hosted bundles, silently dropping
	// `data-*` overrides.
	const fromScript = scriptConfig ?? parseConfigFromScript();

	const appearance = mergeAppearance(remote, fromScript, programmatic);
	const composer: ComposerAppearance = {
		...(remote?.composer ?? {}),
		...compact(fromScript.composer),
		...compact(programmatic?.composer),
	};
	const hasComposer = Object.keys(composer).length > 0;

	const merged: EmbedConfig = {
		token: "",
		...DEFAULTS,
		...compact(remote),
		...compact(fromScript),
		...compact(programmatic),
		...(appearance ? { appearance } : {}),
		...(hasComposer ? { composer } : {}),
	};

	if (typeof merged.dockChipsTrigger === "string") {
		merged.dockChipsTrigger = [merged.dockChipsTrigger];
	}

	if (!merged.token) {
		throw new Error(
			"[Waniwani] Missing required config: `token`. " +
				"Set data-token on the script tag or pass it to Waniwani.chat.init().",
		);
	}

	return merged;
}

function mergeAppearance(
	remote: Partial<EmbedConfig> | undefined,
	fromScript: Partial<EmbedConfig>,
	programmatic: Partial<EmbedConfig> | undefined,
): ChatAppearance | undefined {
	const theme =
		programmatic?.appearance?.theme ??
		fromScript.appearance?.theme ??
		remote?.appearance?.theme;
	const variables: ChatTheme = {
		...(remote?.appearance?.variables ?? {}),
		...(fromScript.appearance?.variables ?? {}),
		...(programmatic?.appearance?.variables ?? {}),
	};
	const hasVars = Object.keys(variables).length > 0;
	const assistantBubble =
		programmatic?.appearance?.assistantBubble ??
		fromScript.appearance?.assistantBubble ??
		remote?.appearance?.assistantBubble;
	if (!theme && !hasVars && assistantBubble === undefined) {
		return undefined;
	}
	const out: ChatAppearance = {};
	if (theme) {
		out.theme = theme;
	}
	if (hasVars) {
		out.variables = variables;
	}
	if (assistantBubble !== undefined) {
		out.assistantBubble = assistantBubble;
	}
	return out;
}
